import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from "@angular/core";
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, skip } from "rxjs/operators";
import { SearchService } from "@core/services/search.service";
// import { CommonService } from "@shared/services/common.service";
import { fadeInOut } from "@shared/skeleton-loader/animation-serivce/app.animation";
import { CustomerProfileService } from "@core/services/customer-profile.service";
import { Router } from "@angular/router";
import { HttpErrorResponse } from "@angular/common/http";
import { SearchHistoryService } from "@core/services/search-history.service";
import { ResourcePermissionService } from "@core/services/resource-permission.service";
import { PermissionService } from "@shared/permission-service.ts/resorces-permission.service";
import { ApiClientService } from "@core/services/api-client.service";
import { AuthService } from "@core/services/auth-new.service";
import { CommonService } from "@shared/services/common.service";
import { ToastService } from "@core/services/toast.service";

interface CustomerResult {
  type: "Customers";
  customerId: number;
  username: string;
  statusId: number;
  enrollNumber: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  enrollmentId: number;
  suffix?: string | null;
  email: string;
  primaryPhone: string;
  matchedProperty?: string;
}

interface OrderResult {
  type: "Orders";
  id: number;
  customerId: number;
  firstName: string;
  lastName: string;
  totalAmount: string;
  paymentStatusId: number;
  orderStatusId: number;
  storeId?: number | null;
  orderTypeId: number;
  orderRemarks?: string | null;
  createdDate: string;
  createdBy: number;
  updateDate?: string | null;
  updatedBy?: number | null;
  isExported: number;
  orderStatus: string;
  matchedProperty?: string;
}

interface SubscriptionResult {
  type: "Subscriptions";
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  customerId: number;
  profileId: number;
  paymentProfileId: number;
  subscriptionId: number;
  subscriptionTypeId: number;
  status: string;
  remarks?: string | null;
  planId: number;
  nextBillingDate: string;
  nextRenewalDate?: string | null;
  intervalLength?: number | null;
  startDate: string;
  endDate?: string | null;
  autoRenew: number;
  createdDate: string;
  updatedDate: string;
  matchedProperty?: string;
}

interface TicketResult {
  type: "Tickets";
  id: number;
  title: string;
  description?: string | null;
  ticketTypeId: number;
  ticketType: string;
  agentId: number;
  groupId?: number | null;
  customerId: number;
  firstName: string;
  lastName: string;
  assigneeId: number;
  departmentId?: number | null;
  statusId: number;
  priorityId: number;
  assignmentType: string;
  createdDate: string;
  createdBy: number;
  dueDate?: string | null;
  updatedDate: string;
  updatedBy: number;
  resolvedBy?: number | null;
  closedBy?: number | null;
  isNotified: number;
  closedDate?: string | null;
  escalationLevel?: number | null;
  parentId?: number | null;
  isCallbackRequired: number;
  callbackDate?: string | null;
  callbackTime?: string | null;
  isActive: number;
  isDeleted: number;
  status: string;
  priority: string;
  matchedProperty?: string;
}

interface PaymentResult {
  type: "Payments";
  id: number;
  amount: number;
  nameOnCard: string;
  firstName: string;
  lastName: string;
  responseCode: string;
  tracId: string;
  refId: string;
  customerId: number;
  invoiceId: number;
  invoiceItemId?: number | null;
  authCode: string;
  avsResultCode: string;
  cvvResultCode: string;
  refTransId: string;
  transHash: string;
  restRequest: string;
  CardNumber: string;
  CardType: string;
  message?: string | null;
  messageCode: string;
  quantity: number;
  address: string;
  createdDate: string;
  createdBy: number;
  source?: string;
  matchedProperty?: string;
}

interface AddressResult {
  type: "Addresses";
  id: number;
  enrollmentId: number;
  serviceAddress1: string;
  serviceAddress2: string;
  serviceUnit?: string | null;
  cityId: number;
  stateId: number;
  zipId: number;
  mailServiceAddress1: string;
  mailServiceAddress2: string;
  mailCityId: number;
  mailStateId: number;
  mailZipId: number;
  isMailAddrSame: string;
  isPermanent?: string | null;
  billingAddress1: string;
  billingAddress2: string;
  billingZipId: number;
  billingCityId: number;
  billingStateId: number;
  shippingAddress1: string;
  shippingAddress2: string;
  shippingZipId: number;
  shippingCityId: number;
  shippingStateId: number;
  isActive: number;
  isDeleted: number;
  createdDate: string;
  updatedBy?: number | null;
  createdBy?: number | null;
  updatedDate?: string | null;
  matchedProperty?: string;
}

type SearchResult =
  | CustomerResult
  | OrderResult
  | SubscriptionResult
  | TicketResult
  | PaymentResult
  | AddressResult;

@Component({
  selector: "app-global-search",
  templateUrl: "./global-search.component.html",
  styleUrls: ["./global-search.component.scss"],
  animations: [fadeInOut],
})
export class GlobalSearchComponent implements OnInit, OnDestroy, OnChanges {
  private readonly minSearchLength = 3;

  searchQuery: string = "";
  recentSearches: any[] = [];
  selectedBadgeId: number | null = 7;
  companySearchHistory: any = [];
  filteredSearches: any[] = [];
  agentId: any;
  @Output() closeGlobalSearch = new EventEmitter<void>();
  badges = [
    { id: 1, type: "Customers", show: true, dsc: "customers" },
    { id: 2, type: "Orders", show: true, dsc: "orders" },
    { id: 3, type: "Subscriptions", show: true, dsc: "subscriptions" },
    { id: 4, type: "Tickets", show: true, dsc: "tickets" },
    { id: 5, type: "Payments", show: true, dsc: "payments" }
  ];

  // Permission control for global search
  hasGlobalSearchPermission: boolean = false;
  @Input() text: any;
  @Input() tabPermissions: Record<string, boolean> = {};
  searchResults: SearchResult[] = [];
  private searchSubscription!: Subscription;
  isSkeletonLoader: boolean = false;
  noResultFound: boolean = false;
  private currentRequestId = 0;
  private controller: AbortController | undefined;
  isRecentSearch: boolean = true;
  showPastSearches: boolean = false;
  isLoadingSearchHistory: boolean = false;

  // Scroll indicator state for searches list
  isSearchesListScrollable: boolean = false;
  isSearchesListScrolledToBottom: boolean = false;

  // Scroll indicator state for search results
  isSearchResultsScrollable: boolean = false;
  isSearchResultsScrolledToBottom: boolean = false;

  // ViewChild for scroll container
  @ViewChild('searchResultsContainer') searchResultsContainer!: ElementRef;
  @ViewChild('searchesListScrollable') searchesListScrollable!: ElementRef;

  // New tab state for Search Results / Customer
  selectedResultTab: 'search-results' | 'customer' = 'search-results';

  // Tab counts from API pagination totals
  searchResultTabCount: number | null = null;
  customerTabCount: number | null = null;

  // Pagination properties
  currentPage: number = 1;
  perPage: number = 5;
  totalPages: number = 0;
  totalResults: number = 0;
  isLoadingMore: boolean = false;
  hasMoreResults: boolean = false;
  orderJsonCodeMap = [
    { code: 'RO_CARD_PENDING_REVIEW_ORDERS', name: 'Pending Review' },
    { code: 'RO_CARD_IN_PROCESS', name: 'Processing' },
    { code: 'RO_CARD_SHIPPED', name: 'Shipped' },
    { code: 'RO_CARD_DELIVERED', name: 'Delivered' },
    { code: 'RO_CARD_CANCELLED', name: 'Cancelled' },
    { code: 'RO_CARD_ON_HOLD', name: 'On Hold' },
    { code: 'RO_CARD_COMPLETED_ORDERS', name: 'Completed' },
    { code: 'RO_CARD_RETURNED', name: 'Returned' },
    { code: 'RO_CARD_REFUNDED', name: 'Refunded' },
    { code: 'RO_CARD_AWAITING_PAYMENT', name: 'Awaiting Payment' },
    { code: 'Payment Received', name: 'Payment Received' },
    { code: 'Payment Failed', name: 'Payment Failed' },
    { code: 'InTransit', name: 'In Transit' },
    { code: 'OP_PENDING_PROVISIONING', name: 'Approved' },
    { code: 'OP_PROVISION_ITEM_ASSIGNED', name: 'Provisioning in progress' },
    { code: 'OP_PROVISIONED', name: 'Completed' },
    { code: 'OP_PROVISION_FAILED', name: 'Failed' },
    { code: 'SH_AWAITING_SHIPMENT', name: 'Awaiting Shipment' },
    { code: 'SH_AWAITING_SHIPMENT_FAILED', name: 'Failed to stage shipment' },
    { code: 'Reviewing', name: 'Reviewing' },
    { code: 'DISPATCHED', name: 'Dispatched' },
    { code: 'RO_CARD_APPROVED_ORDERS', name: 'Approved' },
    { code: 'RO_CARD_REJECTED_ORDERS', name: 'Rejected Orders' },
    { code: 'Payment receive', name: 'Payment Received' },
    { code: 'EQP_NEW_ORDER_CSR', name: 'New Oder CSR' },
    { code: 'STAGING_INITIATED', name: 'Staging Initiated' }
  ];
  orderBadgeGroups = {
    'badge-success': [
      'Delivered',
      'Completed',
      'Approved',
      'Shipped',
      'Payment Received'
    ],
    'badge-danger': [
      'On Hold',
      'Failed',
      'Failed to stage shipment',
      'Rejected Orders',
      'Cancelled'
    ],
    'badge-primary': [
      'Processing',
      'Provisioning in progress',
      'In Transit',
      'Dispatched',
      'Pending Review'
    ],
    'badge-warning': [
      'Escalated',
      'Awaiting Payment',
      'Awaiting Shipment',
      'Reviewing',
      'Staging Initiated'
    ],
    'badge-secondary': [
      'Open',
      'New Oder CSR',
      'Returned',
      'Refunded',
      'Payment Failed'
    ]
  };

  constructor(
    private searchService: SearchService,
    public commonService: CommonService,
    private customerProfileService: CustomerProfileService,
    public router: Router,
    private searchHistoryService: SearchHistoryService,
    private permissionService: ResourcePermissionService,
    private sharedPermissionService: PermissionService,
    private apiClient: ApiClientService,
    private authService: AuthService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) { }

  private isInitialized = false;

  ngOnChanges(changes: SimpleChanges): void {

  }



  async ngOnInit(): Promise<void> {
    if (typeof document !== 'undefined') {
      document.body.classList.add('global-search-open');
    }
    this.isRecentSearch = true;
    this.agentId = this.authService.user()?.userID;

    // Show loading state
    this.isLoadingSearchHistory = true;

    // Load recent searches ONLY from IndexedDB (default tab)
    await this.loadRecentSearchHistory();

    // NOTE: We do NOT load past searches here - they will be loaded only when user clicks Past Searches tab
    // this.refreshSearchHistory(); // REMOVED - API should only be called when Past Searches tab is clicked

    // Ensure filtered searches are updated after loading
    this.updateFilteredSearches();

    this.isLoadingSearchHistory = false;
    this.isInitialized = true;

    // Force change detection to ensure UI updates
    this.cdr.detectChanges();

    console.log('ðŸ”„ Global search component initialized (ngOnInit called) at:', new Date().toLocaleTimeString(), {
      isRecentSearch: this.isRecentSearch,
      selectedBadgeId: this.selectedBadgeId,
      showPastSearches: this.showPastSearches,
      recentSearchesCount: this.recentSearches.length,
      filteredSearchesCount: this.filteredSearches.length
    });

    this.searchSubscription = this.searchService.searchTerm$.pipe(
      skip(1),           // skip the BehaviorSubject's initial replay — ngOnInit already loaded history
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(
      (term) => {
        this.searchQuery = typeof term === "string" ? term : "";
        const trimmedQuery = this.searchQuery.trim();
        console.log("ðŸ” Search term changed:", this.searchQuery, "| isInitialized:", this.isInitialized);
        if (trimmedQuery.length >= this.minSearchLength) {
          console.log("âœï¸ Search query detected - switching to search mode");
          this.isSkeletonLoader = true;
          this.searchResultTabCount = null;
          this.customerTabCount = null;
          this.isRecentSearch = false;
          this.onSearchChange();
        } else if (trimmedQuery.length > 0) {
          console.log(`Waiting for ${this.minSearchLength} characters before calling search API`);
          this.isSkeletonLoader = false;
          this.isRecentSearch = false;
          this.noResultFound = false;
          this.clearSearchResults();
          // Auto-select the first permitted tab as soon as the user starts typing,
          // even before the min-length threshold is reached.
          if (this.selectedBadgeId === 7) {
            const firstPermitted = this.badges.find(b => b.show);
            const badgeType = firstPermitted?.dsc || 'customers';
            const badge = this.badges.find(b => b.type.toLowerCase() === badgeType.toLowerCase() && b.id !== 7);
            if (badge) {
              this.selectedBadgeId = badge.id;
            }
          }
          this.cdr.detectChanges();
        } else {
          console.log("ðŸ  Empty search query - switching to recent search mode");
          this.isSkeletonLoader = false;
          this.isRecentSearch = true;
          this.selectedBadgeId = 7;
          this.clearSearchResults();
          // Reload recent searches from IndexedDB when search is cleared (but not during initialization)
          if (this.isInitialized) {
            this.loadRecentSearchHistory(); // Load from IndexedDB only
          }
          // Force change detection when switching back to recent searches
          this.cdr.detectChanges();
        }
      }
    );

    // Apply tab permissions: use parent-provided map if available, otherwise fetch from API
    if (Object.keys(this.tabPermissions).length > 0) {
      this.badges.forEach(badge => {
        if (badge.dsc in this.tabPermissions) {
          badge.show = this.tabPermissions[badge.dsc];
        }
      });
    } else {
      try {
        const allCategories = await this.sharedPermissionService.getAllResourceCategories();
        const menusCategory = allCategories?.find((c: any) => c.Name === 'MENUS');
        if (menusCategory) {
          const headerMenuType = menusCategory.resource_types?.find((t: any) => t.Name === 'HEADER_MENU');
          const globalSearchResource = headerMenuType?.resources?.find((r: any) => r.Name === 'GOBAL_SEARCH' || r.Name === 'GLOBAL_SEARCH');
          const children = globalSearchResource?.children;
          if (children?.length) {
            // Name → badge dsc mapping (Description can be null so Name is reliable)
            const nameToTabMap: Record<string, string> = {
              'CUSTOMER_TAB':     'customers',
              'ORDER_TAB':        'orders',
              'SUBSCRIPTION_TAB': 'subscriptions',
              'TICKET_TAB':       'tickets',
              'PAYMENT_TAB':      'payments',
            };
            children.forEach((child: any) => {
              const dsc = nameToTabMap[child.Name] ?? child.Description?.toLowerCase();
              const matchingBadge = this.badges.find(b => b.dsc === dsc);
              if (matchingBadge) {
                // resource_permission[0] is the backend-computed effective permission
                matchingBadge.show = child?.resource_permission?.[0]?.CanView === 1;
              }
            });
          }
        }
      } catch (e) {
        console.warn('Could not load tab permissions for global search', e);
      }
    }
  }

  mapToRecentSearchesFormat(arrayOfObjects: any[]): any[] {
    const mappedData: any[] = [];

    arrayOfObjects?.forEach((obj) => {
      try {
        const parsedResponse = JSON.parse(obj.response);

        // Extract the first item if it's an array for easier template access
        let itemData = parsedResponse;
        if (Array.isArray(parsedResponse) && parsedResponse.length > 0) {
          itemData = parsedResponse[0];
        }

        const mappedItem = {
          searchTerm: obj.search_term,
          activeTab: obj.search_tab,
          items: parsedResponse,
          itemData: itemData, // Cache the first item for template use
          timestamp: obj.created_at,
          id: obj.id,
          // Pre-compute display values to avoid method calls in template
          displayId: this.extractId(itemData, obj.search_tab),
          displayName: this.extractName(itemData, obj.search_tab),
        };

        mappedData.push(mappedItem);
      } catch (error) {
        console.error('Error parsing JSON string:', error);
      }
    });

    return mappedData;
  }

  /**
   * Extract ID from item data based on tab type
   */
  private extractId(itemData: any, activeTab: string): string | number {
    if (!itemData) return '';

    const tab = activeTab?.toLowerCase();
    
    switch (tab) {
      case 'customers':
        return itemData.customerId || itemData.CustomerID || itemData.id || '';
      case 'orders':
        return itemData.customerId || itemData.CustomerID || itemData.id || '';
      case 'subscriptions':
        return itemData.customerId || itemData.CustomerID || itemData.id || '';
      case 'tickets':
        return itemData.customerId || itemData.CustomerID || itemData.id || '';
      case 'payments':
        return itemData.customerId || itemData.CustomerID || itemData.id || '';
      default:
        return itemData.id || '';
    }
  }

  /**
   * Extract name/title from item data based on tab type
   */
  private extractName(itemData: any, activeTab: string): string {
    if (!itemData) return 'Unknown';

    const tab = activeTab?.toLowerCase();

    // Helper function to capitalize first letter of each word
    const toTitleCase = (str: string): string => {
      return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    };

    switch (tab) {
      case 'customers':
        const firstName = itemData.firstName || itemData.FirstName || '';
        const lastName = itemData.lastName || itemData.LastName || '';

        if (firstName && lastName) {
          return `${toTitleCase(firstName)} ${toTitleCase(lastName)}`.trim();
        }

        const CardNumber = itemData.CardNumber || itemData.CardNumber || '';
        if (CardNumber) {
          return CardNumber;
        }

        return 'Unknown Customer';

      case 'orders':
        const orderFirstName = itemData.firstName || itemData.FirstName || '';
        const orderLastName = itemData.lastName || itemData.LastName || '';
        if (orderFirstName && orderLastName) {
          return `${toTitleCase(orderFirstName)} ${toTitleCase(orderLastName)}`.trim();
        }
        return `Order #${itemData.id || itemData.OrderID || ''}`;

      case 'subscriptions':
        const subFirstName = itemData.firstName || itemData.FirstName || '';
        const subLastName = itemData.lastName || itemData.LastName || '';
        if (subFirstName && subLastName) {
          return `${toTitleCase(subFirstName)} ${toTitleCase(subLastName)}`.trim();
        }
        // Fallback to email if name not available
        const subEmail = itemData.email || itemData.Email || '';
        if (subEmail) {
          return subEmail;
        }
        return `Subscription #${itemData.subscriptionId || itemData.SubscriptionId || ''}`;

      case 'tickets':
        const ticketFirstName = itemData.firstName || itemData.FirstName || '';
        const ticketLastName = itemData.lastName || itemData.LastName || '';
        if (ticketFirstName && ticketLastName) {
          return `${toTitleCase(ticketFirstName)} ${toTitleCase(ticketLastName)}`.trim();
        }
        return itemData.title || itemData.Title || `Ticket #${itemData.id || itemData.TicketID || ''}`;

      case 'payments':
        const paymentFirstName = itemData.firstName || itemData.FirstName || '';
        const paymentLastName = itemData.lastName || itemData.LastName || '';
        if (paymentFirstName && paymentLastName) {
          return `${toTitleCase(paymentFirstName)} ${toTitleCase(paymentLastName)}`.trim();
        }
        return `Payment #${itemData.id || itemData.PaymentID || ''}`;

      default:
        return `Record #${itemData.id || ''}`;
    }
  }

  showRecentSearch() {
    this.selectedBadgeId = 7;
    this.isRecentSearch = true;
    this.clearSearchResults();
  }

  async loadRecentSearchHistory(): Promise<void> {
    try {
      this.isLoadingSearchHistory = true;
      const history = await this.searchHistoryService.getAllSearchRecords();

      // Pre-process the history items to add display values
      this.recentSearches = history.map((item: any) => {
        // Normalise activeTab to lowercase so old mixed-case records still work
        const normalisedTab = (item.activeTab || '').toLowerCase();
        // Normalise items to array
        const normalisedItems = Array.isArray(item.items)
          ? item.items
          : item.items ? [item.items] : [];
        const itemData = normalisedItems.length > 0 ? normalisedItems[0] : null;

        return {
          ...item,
          activeTab: normalisedTab,
          items: normalisedItems,
          itemData: itemData,
          displayId: this.extractId(itemData, normalisedTab),
          displayName: this.extractName(itemData, normalisedTab),
        };
      });

      console.log("recentSearches: ", this.recentSearches);
      this.updateFilteredSearches();

      // Trigger change detection after data is loaded
      this.cdr.markForCheck();

      // Check if the searches list is scrollable
      this.checkSearchesListScrollable();
    } catch (error) {
      console.error('Error loading recent search history:', error);
    } finally {
      this.isLoadingSearchHistory = false;
      // Trigger change detection when loading completes
      this.cdr.markForCheck();
    }
  }

  /**
   * Refresh search history (both recent and company history)
   */
  async refreshSearchHistory(): Promise<void> {
    try {
      // Wait until agentId is available
      let agentId = this.agentId || this.authService.user()?.userID;

      // If still no agentId, wait a bit and retry
      if (!agentId) {
        await new Promise(resolve => setTimeout(resolve, 100));
        agentId = this.authService.user()?.userID;
      }

      if (!agentId) {
        console.warn('Agent ID not available, skipping search history refresh');
        return;
      }

      console.log('Agent ID for search history: ', agentId);
      console.log('Fetching search history for Agent ID: ', this.authService.user());

      // Fetch company search history from the API using toPromise()
      const data = await this.searchService.getCompanySearchHistory(agentId).toPromise();

      if (data) {
        this.companySearchHistory = this.mapToRecentSearchesFormat(data?.data);
        console.log('Mapped Company Search History: ', this.companySearchHistory);
        this.updateFilteredSearches();
      }

      // Load recent search history from IndexedDB
      await this.loadRecentSearchHistory();

    } catch (error) {
      console.error('Error fetching company search history:', error);
    }
  }

  /**
   * Refresh only past search history from API
   * This method ALWAYS calls the API to get fresh data from the server
   */
  async refreshPastSearchHistory(): Promise<void> {
    try {
      this.isLoadingSearchHistory = true;
      let agentId = this.agentId || this.authService.user()?.userID;

      if (!agentId) {
        console.warn('Agent ID not available, skipping past search history refresh');
        this.isLoadingSearchHistory = false;
        return;
      }

      console.log('ðŸ“¡ Refreshing Past Search History for Agent ID: ', agentId);
      console.log('ðŸ“¡ Calling /getGlobalSearchLog API with forceRefresh=true to bypass cache');

      // IMPORTANT: forceRefresh=true ensures the API is ALWAYS called, bypassing the cache
      const data = await this.searchService.getCompanySearchHistory(agentId, true).toPromise();

      console.log('âœ… API Response received:', data);

      if (data && data.data) {
        this.companySearchHistory = this.mapToRecentSearchesFormat(data?.data);
        console.log('âœ… Refreshed Past Search History count:', this.companySearchHistory.length);
        console.log('âœ… Refreshed Past Search History data:', this.companySearchHistory);
        this.updateFilteredSearches();
      } else {
        console.warn('âš ï¸ No data received from API');
        // Clear company search history if no data
        this.companySearchHistory = [];
        this.updateFilteredSearches();
      }

    } catch (error) {
      console.error('âŒ Error refreshing past search history:', error);
      // Clear company search history on error
      this.companySearchHistory = [];
      this.updateFilteredSearches();
    } finally {
      this.isLoadingSearchHistory = false;
      // Check if the searches list is scrollable
      this.checkSearchesListScrollable();
    }
  }

  switchToRecentSearches(): void {
    this.showPastSearches = false;
    // Reload recent searches from IndexedDB
    this.loadRecentSearchHistory();
    console.log('Switched to Recent Searches');
    // Check if the searches list is scrollable
    this.checkSearchesListScrollable();
  }

  async switchToPastSearches(): Promise<void> {
    console.log('ðŸ”„ Past Searches tab clicked - initiating API call');
    // Set loading state immediately
    this.isLoadingSearchHistory = true;
    // Set the flag first so updateFilteredSearches works correctly
    this.showPastSearches = true;
    // Clear current filtered searches to show loading state
    this.updateFilteredSearches();
    // Reload past searches from API (this will ALWAYS call the API with forceRefresh=true)
    await this.refreshPastSearchHistory();
    console.log('âœ… Switched to Past Searches - API call completed');
    // Check if the searches list is scrollable
    this.checkSearchesListScrollable();
  }

  getFilteredSearches(): any[] {
    return this.filteredSearches;
  }

  private updateFilteredSearches(): void {
    const rawSearches = this.showPastSearches
      ? this.companySearchHistory
      : this.recentSearches;

    // Group the searches by tab and respective ID
    this.filteredSearches = this.groupSearchRecords(rawSearches);

    console.log('Updated filtered searches:', {
      showPastSearches: this.showPastSearches,
      filteredCount: this.filteredSearches.length,
      recentCount: this.recentSearches.length,
      pastCount: this.companySearchHistory.length
    });
  }

  /**
   * Group search records by tab and respective ID
   * For same tab + same ID, show only one card with count badge
   */
  private groupSearchRecords(searches: any[]): any[] {
    if (!searches || searches.length === 0) {
      return [];
    }

    // Create a map to group records: key = "tab_id", value = array of records
    const groupMap = new Map<string, any[]>();

    searches.forEach((search) => {
      const tab = search.activeTab?.toLowerCase();
      const itemData = search.itemData || search.items;

      if (!tab || !itemData) {
        return;
      }

      // Extract the respective ID based on tab type
      let respectiveId: string | number = '';

      switch (tab) {
        case 'customers':
          respectiveId = itemData.customerId || itemData.CustomerID || '';
          break;
        case 'orders':
          respectiveId = itemData.id || itemData.OrderID || '';
          break;
        case 'subscriptions':
          respectiveId = itemData.subscriptionId || itemData.SubscriptionId || '';
          break;
        case 'tickets':
          respectiveId = itemData.id || itemData.TicketID || '';
          break;
        case 'payments':
          respectiveId = itemData.id || itemData.PaymentID || '';
          break;
        default:
          respectiveId = itemData.id || '';
      }

      // Create a unique key for grouping
      const groupKey = `${tab}_${respectiveId}`;

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, []);
      }

      groupMap.get(groupKey)?.push(search);
    });

    // Convert grouped map to array and process each group
    const groupedSearches: any[] = [];

    groupMap.forEach((records, key) => {
      if (records.length === 0) {
        return;
      }

      // Sort records by timestamp (newest first)
      records.sort((a, b) => {
        const dateA = new Date(a.timestamp).getTime();
        const dateB = new Date(b.timestamp).getTime();
        return dateB - dateA; // Descending order (latest first)
      });

      // Take the latest record as the display record
      const displayRecord = { ...records[0] };

      // Add grouping metadata
      displayRecord.searchCount = records.length;
      displayRecord.allSearchTimestamps = records.map((r) => ({
        timestamp: r.timestamp,
        searchTerm: r.searchTerm
      }));

      groupedSearches.push(displayRecord);
    });

    // Sort the grouped searches by latest timestamp
    groupedSearches.sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateB - dateA; // Descending order (latest first)
    });

    return groupedSearches;
  }

  ngOnDestroy(): void {
    console.log('ðŸ—‘ï¸ Global search component destroyed (ngOnDestroy called) at:', new Date().toLocaleTimeString());
    if (this.controller) {
      this.controller.abort();
    }
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (typeof document !== 'undefined') {
      document.body.classList.remove('global-search-open');
    }
  }

  startNewRequest() {
    this.currentRequestId++;
    if (this.controller) {
      this.controller.abort();
      this.isSkeletonLoader = true;
      this.searchResultTabCount = null;
      this.customerTabCount = null;
    }
    this.controller = new AbortController();
  }

  onClickRecentSearch() {
    this.searchResults = [];
    this.isRecentSearch = true;
    this.selectedBadgeId = 7;
    this.clearSearchResults();
    this.noResultFound = false;
    console.log("recent search: ", this.recentSearches);
  }

  async onSearchChange(isLoadMore: boolean = false) {
    const trimmedQuery = this.searchQuery.trim();

    if (!trimmedQuery) {
      this.clearSearchResults();
      return;
    }

    if (trimmedQuery.length < this.minSearchLength) {
      this.isSkeletonLoader = false;
      this.isLoadingMore = false;
      this.noResultFound = false;
      this.clearSearchResults();
      return;
    }

    if (this.selectedBadgeId === 7) {
      // Auto-select the first permitted tab; fallback to customers if none found
      const firstPermitted = this.badges.find(b => b.show);
      const badgeType = firstPermitted?.dsc || 'customers';
      const badge = this.badges.find(b => b.type.toLowerCase() === badgeType.toLowerCase() && b.id !== 7);
      if (badge) {
        this.selectedBadgeId = badge.id;
        this.isRecentSearch = false;
      }
    }

    this.isRecentSearch = false;

    // Reset pagination if not loading more
    if (!isLoadMore) {
      this.currentPage = 1;
      this.searchResults = [];
      this.startNewRequest();
    } else {
      this.isLoadingMore = true;
    }

    const requestId = this.currentRequestId;
    const activeTab = this.badges.find(
      (badge) => badge.id === this.selectedBadgeId
    )?.type;

    if (!this.searchQuery.trim() || !activeTab) {
      this.isSkeletonLoader = false;
      this.isLoadingMore = false;
      return;
    }

    try {
      // Build search options with filter if Customer tab is selected
      const searchOptions: any = {
        signal: this.controller?.signal,
        page: this.currentPage,
        perPage: this.perPage
      };

      // Add filter parameter when Customer tab is active
      if (this.selectedResultTab === 'customer') {
        searchOptions.filter = 'customer-specific';
      }

      const res: any = await this.searchService.globalSearch(
        trimmedQuery,
        activeTab,
        searchOptions
      );

      if (this.currentRequestId === requestId && res?.Status) {
        console.log('Full API Response:', res);
        console.log('res.data:', res.data);
        console.log('Active Tab:', activeTab);

        // Handle new response structure
        // Structure: { Status, data: { customers: {data: [], pagination: {}}, orders: {...}, ... } }
        let dataArray = [];
        let pagination = null;

        const tabKey = activeTab.toLowerCase();
        console.log('Looking for tab key:', tabKey);

        if (res.data && res.data[tabKey]) {
          // New structure with tab-specific data
          const tabData = res.data[tabKey];
          console.log('Tab data found:', tabData);

          if (tabData.data && Array.isArray(tabData.data)) {
            dataArray = tabData.data;
            pagination = tabData.pagination;
          } else if (Array.isArray(tabData)) {
            // Fallback if tab data is directly an array
            dataArray = tabData;
          }
        } else if (res.data?.data && Array.isArray(res.data.data)) {
          // Alternative structure: { Status, data: { data: [], pagination: {} } }
          dataArray = res.data.data;
          pagination = res.data.pagination;
        }

        console.log('Extracted dataArray:', dataArray, 'Length:', dataArray.length);
        console.log('Pagination:', pagination);

        const newResults = this.mapDataToResults(dataArray, activeTab);
        console.log('Mapped results:', newResults, 'Length:', newResults.length);

        if (isLoadMore) {
          this.searchResults = [...this.searchResults, ...newResults];
        } else {
          this.searchResults = newResults;
        }

        // Update pagination info
        if (pagination) {
          this.totalPages = pagination.last_page;
          this.totalResults = pagination.total;
          this.hasMoreResults = this.currentPage < this.totalPages;

          // Update tab counts
          if (this.selectedResultTab === 'customer') {
            this.customerTabCount = pagination.total;
          } else {
            this.searchResultTabCount = pagination.total;
          }

          console.log('Pagination Updated:', {
            currentPage: this.currentPage,
            totalPages: this.totalPages,
            totalResults: this.totalResults,
            hasMoreResults: this.hasMoreResults,
            perPage: this.perPage
          });
        } else {
          this.hasMoreResults = false;
          console.warn('No pagination info found in response');
        }

        this.noResultFound = this.searchResults.length === 0;
      } else {
        console.log("Stale response discarded or Status is false", res);
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Request aborted:", requestId);
      } else {
        this.handleError(error, "Failed to fetch search results");
      }
    } finally {
      if (this.currentRequestId === requestId) {
        this.isSkeletonLoader = false;
        this.isLoadingMore = false;
        // Check if search results container is scrollable
        this.checkSearchResultsScrollable();
      }
    }
  }

  private mapDataToResults(data: any, type: string): SearchResult[] {
    console.log('mapDataToResults called with:', { data, type, isArray: Array.isArray(data) });

    // Handle new API structure where data is directly an array
    if (!Array.isArray(data)) {
      console.warn('Data is not an array:', data);
      return [];
    }

    console.log(`Mapping ${data.length} items for type: ${type}`);

    switch (type) {
      case "Customers":
        return data.map((customer: any): CustomerResult => ({
          type: "Customers",
          customerId: customer.CustomerId || customer.customerId,
          username: customer.Username || customer.username,
          statusId: customer.StatusID || customer.statusId,
          enrollNumber: customer.EnrollNumber || customer.enrollNumber,
          firstName: customer.FirstName || customer.firstName,
          middleName: customer.MiddleName || customer.middleName,
          lastName: customer.LastName || customer.lastName,
          enrollmentId: customer.EnrollmentID || customer.enrollmentId,
          suffix: customer.Suffix || customer.suffix,
          email: customer.Email || customer.email,
          primaryPhone: customer.PrimaryPhone || customer.primaryPhone,
          matchedProperty: customer.matchedProperty || customer.MatchedProperty,
        }));

      case "Orders":
        return data.map((order: any): OrderResult => ({
          type: "Orders",
          id: order.id,
          customerId: order.CustomerId,
          firstName: order.FirstName || '',
          lastName: order.LastName || '',
          totalAmount: order.TotalAmount,
          paymentStatusId: order.PaymentStatusId,
          orderStatusId: order.OrderStatusId,
          storeId: order.StoreID,
          orderTypeId: order.OrderTypeID,
          orderRemarks: order.OrderRemarks,
          createdDate: order.CreatedDate,
          createdBy: order.CreatedBy,
          updateDate: order.UpdateDate,
          updatedBy: order.UpdatedBy,
          isExported: order.IsExported,
          orderStatus: this.orderJsonCodeMap.find((status) => status.code === order.OrderStatus)?.name || order.OrderStatus,
          matchedProperty: order.matchedProperty || order.MatchedProperty,
        }));

      case "Subscriptions":
        return data.map((subscription: any): SubscriptionResult => ({
          type: "Subscriptions",
          id: subscription.id,
          email: subscription.Email,
          firstName: subscription.FirstName || '',
          lastName: subscription.LastName || '',
          customerId: subscription.CustomerId,
          profileId: subscription.ProfileId,
          paymentProfileId: subscription.PaymentProfileId,
          subscriptionId: subscription.SubscriptionId,
          subscriptionTypeId: subscription.SubscriptionTypeId,
          status: subscription.Status,
          remarks: subscription.Remarks,
          planId: subscription.PlanId,
          nextBillingDate: subscription.NextBillingDate,
          nextRenewalDate: subscription.NextRenewalDate,
          intervalLength: subscription.IntervalLength,
          startDate: subscription.StartDate,
          endDate: subscription.EndDate,
          autoRenew: subscription.AutoRenew,
          createdDate: subscription.CreatedDate,
          updatedDate: subscription.UpdatedDate,
          matchedProperty: subscription.matchedProperty || subscription.MatchedProperty,
        }));

      case "Tickets":
        return data.map((ticket: any): TicketResult => ({
          type: "Tickets",
          id: ticket.id,
          title: ticket.Title,
          description: ticket.Description,
          ticketTypeId: ticket.TicketTypeID,
          ticketType: ticket.Type || '',
          agentId: ticket.AgentID,
          groupId: ticket.GroupID,
          customerId: ticket.CustomerID,
          firstName: ticket.FirstName || '',
          lastName: ticket.LastName || '',
          assigneeId: ticket.AssigneeID,
          departmentId: ticket.DepartmentID,
          statusId: ticket.StatusID,
          priorityId: ticket.PriorityId,
          assignmentType: ticket.AssignmentType,
          createdDate: ticket.CreatedDate,
          createdBy: ticket.CreatedBy,
          dueDate: ticket.DueDate,
          updatedDate: ticket.UpdatedDate,
          updatedBy: ticket.UpdatedBy,
          resolvedBy: ticket.ResolvedBy,
          closedBy: ticket.ClosedBy,
          isNotified: ticket.IsNotified,
          closedDate: ticket.ClosedDate,
          escalationLevel: ticket.EscalationLevel,
          parentId: ticket.ParentId,
          isCallbackRequired: ticket.IsCallbackRequired,
          callbackDate: ticket.CallbackDate,
          callbackTime: ticket.CallbackTime,
          isActive: ticket.IsActive,
          isDeleted: ticket.IsDeleted,
          status: ticket.Status,
          priority: ticket.Priority,
          matchedProperty: ticket.matchedProperty || ticket.MatchedProperty,
        }));

      case "Payments":
        return data.map((payment: any): PaymentResult => ({
          type: "Payments",
          id: payment.id,
          amount: payment.amount || payment.Amount,
          nameOnCard: payment.name_on_card || payment.NameOnCard || '',
          firstName: payment.FirstName || payment.firstName || '',
          lastName: payment.LastName || payment.lastName || '',
          responseCode: payment.response_code || payment.ResponseCode || '',
          tracId: payment.txn_id || payment.TracID || '',
          refId: payment.ref_id || payment.RefID || '',
          customerId: payment.customer_id || payment.CustomerID,
          invoiceId: payment.invoice_id || payment.InvoiceID,
          invoiceItemId: payment.invoice_item_id || payment.InvoiceItemID,
          authCode: payment.auth_code || payment.AuthCode || '',
          avsResultCode: payment.avs_result_code || payment.AvsResultCode || '',
          cvvResultCode: payment.cvv_result_code || payment.CvvResultCode || '',
          refTransId: payment.ref_trans_id || payment.RefTransID || '',
          transHash: payment.trans_hash || payment.TransHash || '',
          restRequest: payment.rest_request || payment.RestRequest || '',
          CardNumber: payment.CardNumber || payment.AccountNumber || '',
          CardType: payment.CardType || payment.AccountType || '',
          message: payment.message || payment.Message,
          messageCode: payment.message_code || payment.MessageCode || '',
          quantity: payment.quantity || payment.Quantity || 0,
          address: payment.address || payment.Address || '',
          createdDate: payment.created_at || payment.CreatedDate,
          createdBy: payment.created_by || payment.CreatedBy,
          source: payment.source || payment.Source || 'pos_payments',
          matchedProperty: payment.matchedProperty || payment.MatchedProperty || payment.filter,
        }));

      // case "addresses":
      //   return (
      //     data.addresses?.map(
      //       (address: any): AddressResult => ({
      //         type: "Addresses",
      //         id: address.id,
      //         enrollmentId: address.EnrollmentID,
      //         serviceAddress1: address.ServiceAddress1,
      //         serviceAddress2: address.ServiceAddress2,
      //         serviceUnit: address.ServiceUnit,
      //         cityId: address.CityID,
      //         stateId: address.StateID,
      //         zipId: address.ZipID,
      //         mailServiceAddress1: address.MailServiceAddress1,
      //         mailServiceAddress2: address.MailServiceAddress2,
      //         mailCityId: address.MailCityID,
      //         mailStateId: address.MailStateID,
      //         mailZipId: address.MailZipID,
      //         isMailAddrSame: address.isMailAddrSame,
      //         isPermanent: address.isPermanent,
      //         billingAddress1: address.BillingAddress1,
      //         billingAddress2: address.BillingAddress2,
      //         billingZipId: address.BillingZipID,
      //         billingCityId: address.BillingCityID,
      //         billingStateId: address.BillingStateID,
      //         shippingAddress1: address.ShippingAddress1,
      //         shippingAddress2: address.ShippingAddress2,
      //         shippingZipId: address.ShippingZipID,
      //         shippingCityId: address.ShippingCityID,
      //         shippingStateId: address.ShippingStateID,
      //         isActive: address.IsActive,
      //         isDeleted: address.IsDeleted,
      //         createdDate: address.CreatedDate,
      //         updatedBy: address.UpdatedBy,
      //         createdBy: address.CreatedBy,
      //         updatedDate: address.UpdatedDate,
      //       })
      //     ) || []
      //   );
      default:
        return [];
    }
  }

  triggerClose() {
    this.closeGlobalSearch.emit();
  }

  async onResultClick(result: SearchResult) {
    const activeTab = this.badges.find((badge) => badge.id === this.selectedBadgeId)?.type || '';

    try {
      // Save immediately so the record is in IndexedDB before navigation/component destroy
      await this.searchHistoryService.saveSearchRecordImmediate(
        this.searchQuery,
        activeTab,
        [result]  // always wrap as array for consistent retrieval format
      );

      switch (result.type) {
        case "Customers":
          await this.handleCustomerClick(result as CustomerResult);
          break;
        case "Orders":
          await this.handleOrderClick(result as OrderResult);
          break;
        case "Subscriptions":
          await this.handleSubscriptionClick(result as SubscriptionResult);
          break;
        case "Tickets":
          await this.handleTicketClick(result as TicketResult);
          break;
        case "Payments":
          await this.handlePaymentClick(result as PaymentResult);
          break;
        case "Addresses":
          await this.handleAddressClick(result as AddressResult);
          break;
        default:
          console.log("Unknown type");
          break;
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  private async handleCustomerClick(result: CustomerResult) {
    const customerData = { search: result.customerId, page: "1" };
    const customerResponse: any = await this.searchService.searchCustomer(customerData);

    if (customerResponse?.Status) {
      this.searchService.setSearchData(customerResponse.data);
      await this.customerProfileService.setCustomerFromSearchResult(
        customerResponse.data.data[0]
      );
      this.searchService.updateSearchTerm(
        customerResponse.data.data[0]?.CustomerID
      );
      this.router.navigate(["/customers/dashboard"], {
        queryParams: { customerId: result.customerId }
      });
      this.closeGlobalSearch.emit();
    } else {
      this.toastService.error("Error", customerResponse.message || "Failed to fetch customer data");
    }
  }

  private async handleOrderClick(result: OrderResult) {
    const order = { orderId: result.id, customerId: result.customerId };
    this.goToDetails(order);
    this.clearSearchResults();
    this.closeGlobalSearch.emit();
  }

  private async handleSubscriptionClick(result: SubscriptionResult) {
    console.log('Navigating to subscription - ID:', result.subscriptionId, 'Customer ID:', result.customerId);
    this.router
      .navigateByUrl("/reload", { skipLocationChange: true })
      .then(() => {
        this.router.navigate(["/subscriptions"], {
          queryParams: {
            subscriptionId: result.subscriptionId,
            customerId: result.customerId
          },
        });
      });
    this.clearSearchResults();
    this.closeGlobalSearch.emit();
  }

  private async handlePaymentClick(result: PaymentResult) {
    console.log("PaymentResult : ", result);
    this.router
      .navigateByUrl("/reload", { skipLocationChange: true })
      .then(() => {
         this.router.navigate(['/payments'], {
    queryParams: {
      paymentId: result.id,
      source: result.source || 'pos_payments',
      CustomerId: result.customerId
    }
  });

      });
    this.clearSearchResults();
    this.closeGlobalSearch.emit();
  }

  private async handleAddressClick(result: AddressResult) {
    this.router
      .navigateByUrl("/reload", { skipLocationChange: true })
      .then(() => {
        this.router.navigate(["/ticket-system"], {
          queryParams: { ticketId: result.id },
        });
      });
    this.clearSearchResults();
    this.closeGlobalSearch.emit();
  }

  private async handleTicketClick(result: TicketResult) {
    this.router
      .navigateByUrl("/reload", { skipLocationChange: true })
      .then(() => {
        this.router.navigate(['/ticket-system'], {
    queryParams: { ticketId: result.id }
  });
      });
    this.clearSearchResults();
    this.closeGlobalSearch.emit();
  }

  private handleError(
    error: any,
    fallbackMessage: string = "An error occurred"
  ) {
    if (error?.Status === false) {
      this.toastService.error(
        "Error",
        error.message || fallbackMessage,
      );
    } else if (error instanceof HttpErrorResponse) {
      let message = `Error ${error.status}: ${error.statusText}`;
      console.log("message: ", message);
      this.toastService.error(
        "Error",
        message || fallbackMessage,
      );
    } else {
      this.toastService.error("Error", fallbackMessage);
    }
    this.searchService.updateSearchTerm("");
    this.closeGlobalSearch.emit();
  }

  goToDetails(order: any) {
    this.router
      .navigateByUrl("/reload", { skipLocationChange: true })
      .then(() => {
        this.router.navigate(
          ['/awaiting-shipment'],
          {
            queryParams: {
              orderId: order.orderId,
              customerId: order.customerId  // Add customerId to query params
            },
            state: { order },
          }
        );
      });
  }

  setSearchQuery(type: string) {
    const badge = this.badges.find(
      (badge) => badge.type.toLowerCase() === type.toLowerCase() && badge.id !== 7
    );
    if (badge) {
      this.selectedBadgeId = badge.id;
      if (badge.id === 1 && this.selectedResultTab === 'customer') {
        this.selectedResultTab = 'search-results';
      }
      this.isRecentSearch = false;
      this.onSearchChange().catch((err) => this.handleError(err));
    }
  }

  clearSearchResults() {
    this.searchResults = [];
    this.currentPage = 1;
    this.totalPages = 0;
    this.totalResults = 0;
    this.hasMoreResults = false;
    this.searchResultTabCount = null;
    this.customerTabCount = null;
  }

  /**
   * Switch between Search Results and Customer tabs
   */
  onResultTabChange(tab: 'search-results' | 'customer') {
    if (this.selectedResultTab === tab) {
      return; // Already on this tab
    }

    this.selectedResultTab = tab;

    // Show skeleton loader while fetching new results
    this.isSkeletonLoader = true;
    this.searchResultTabCount = null;
    this.customerTabCount = null;

    // Re-fetch results with the new filter
    if (this.searchQuery.trim()) {
      this.onSearchChange().catch((err) => this.handleError(err));
    }
  }

  /**
   * Load more search results (for scroll pagination)
   */
  async loadMoreResults() {
    if (this.isLoadingMore || !this.hasMoreResults) {
      return;
    }

    this.currentPage++;
    await this.onSearchChange(true);
  }

  /**
   * Handle scroll event for infinite scroll
   */
  onSearchResultsScroll(event: Event) {
    const element = event.target as HTMLElement;

    // Verify we have the element
    if (!element) {
      console.warn('No scroll element found');
      return;
    }

    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    const scrollPosition = scrollTop + clientHeight;
    const scrollPercentage = (scrollPosition / scrollHeight) * 100;

    // Distance from bottom
    const distanceFromBottom = scrollHeight - scrollPosition;

    // Update scroll indicator state (within 10px threshold)
    this.isSearchResultsScrolledToBottom = distanceFromBottom <= 10;

    console.log('Scroll Event Fired:', {
      scrollTop: scrollTop,
      clientHeight: clientHeight,
      scrollHeight: scrollHeight,
      scrollPosition: scrollPosition,
      scrollPercentage: scrollPercentage.toFixed(2) + '%',
      distanceFromBottom: distanceFromBottom,
      hasMoreResults: this.hasMoreResults,
      isLoadingMore: this.isLoadingMore,
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      resultsCount: this.searchResults.length
    });

    // Load more when within 100px of bottom (more aggressive trigger)
    if (distanceFromBottom < 100 && this.hasMoreResults && !this.isLoadingMore) {
      console.log('ðŸš€ Loading more results...');
      this.loadMoreResults();
    }

    // Trigger change detection for scroll indicator
    this.cdr.detectChanges();
  }

  colorClasses = [
    "blue",
    "purple",
    "yellow",
    "green",
    "pink",
    "orange",
    "red",
    "teal",
  ];

  getColorClassForType(index: number): string {
    return this.colorClasses[index % this.colorClasses.length];
  }

  hasSearchResults(): boolean {
    return this.searchResults && this.searchResults.length > 0;
  }


  public async handleResultClick(
    id: number | { id: number; tracId?: string; source?: string; customerId?: number },
    tabName: string,
    customerId?: number,
    searchTerm?: string,
    searchObject?: any
  ) {
    const normalisedTab = tabName.toLowerCase();

    // Normalise items to array for consistent storage format
    const rawItems = searchObject?.items;
    const normalisedItems = Array.isArray(rawItems) ? rawItems : rawItems ? [rawItems] : [];

    // Save immediately BEFORE navigation so the record lands in IndexedDB first
    await this.searchHistoryService.saveSearchRecordImmediate(
      searchTerm || '',
      normalisedTab,
      normalisedItems
    );

    switch (normalisedTab) {
      case "customers":
        await this.handleRecentCustomerClick(id as number, normalisedTab);
        break;
      case "orders":
        await this.handleRecentOrderClick(id as number, normalisedTab, customerId);
        break;
      case "subscriptions":
        await this.handleRecentSubscriptionClick(id as number, normalisedTab, customerId);
        break;
      case "tickets":
        await this.handleRecentTicketClick(id as number, normalisedTab);
        break;
      case "payments":
        if (typeof id === "object" && id.id) {
          await this.handleRecentPaymentClick({
            id: id.id,
            source: id.source,
            customerId: id.customerId
          });
        } else {
          console.error("Invalid payment data passed to handleResultClick:", id);
        }
        break;
      default:
        console.log(`Unknown tab name: ${normalisedTab}`);
        break;
    }
  }

  private async handleRecentPaymentClick(paymentData: {
    id: number;
    source?: string;
    customerId?: number;
  }) {
    this.router
      .navigateByUrl("/reload", { skipLocationChange: true })
      .then(() => {
        this.router.navigate(['/payments'], {
          queryParams: {
            paymentId: paymentData.id,
            source: paymentData.source || 'pos_payments',
            CustomerId: paymentData.customerId
          }
        });
      });
    this.clearSearchResults();
    this.closeGlobalSearch.emit();
  }

  private async handleRecentCustomerClick(id: number, tabName: string) {
    try {
      const customerData = { search: id, page: "1" };
      console.log("customerData:", customerData);
      const customerResponse: any = await this.searchService.searchCustomer(customerData);

      if (customerResponse?.Status) {
        this.searchService.setSearchData(customerResponse.data);
        await this.customerProfileService.setCustomerFromSearchResult(
          customerResponse.data.data[0]
        );
        this.searchService.updateSearchTerm(
          customerResponse.data.data[0]?.CustomerID
        );
        this.router.navigate(["/customers/dashboard"], {
          queryParams: { customerId: id }
        });
        this.closeGlobalSearch.emit();
      } else {
        this.toastService.error(
          `Error in ${tabName}`,
          customerResponse.message,
        );
      }
    } catch (error) {
      this.toastService.error(
        `Error in ${tabName}`,
        "Failed to load customer details",
      );
    }
  }

  private async handleRecentOrderClick(
    id: number,
    tabName: string,
    customerId?: number
  ) {
    try {
      const order = { orderId: id, customerId: customerId };
      this.goToDetails(order);
      this.clearSearchResults();
      this.closeGlobalSearch.emit();
    } catch (error) {
      this.toastService.error(
        `Error in ${tabName}`,
        "Failed to load order details",
      );
    }
  }

  private async handleRecentSubscriptionClick(id: number, tabName: string, customerId?: number) {
    try {
      console.log('Subscription ID: ', id, 'Customer ID: ', customerId);
      this.router
        .navigateByUrl("/reload", { skipLocationChange: true })
        .then(() => {
          this.router.navigate(["/subscriptions"], {
            queryParams: {
              subscriptionId: id,
              customerId: customerId
            },
          });
        });
      this.clearSearchResults();
      this.closeGlobalSearch.emit();
    } catch (error) {
      this.toastService.error(
        `Error in ${tabName}`,
        "Failed to load subscription details",
      );
    }
  }

  private async handleRecentTicketClick(id: number, tabName: string) {
    try {
      this.router
        .navigateByUrl("/reload", { skipLocationChange: true })
        .then(() => {
          this.router.navigate(["/ticket-system"], {
            queryParams: { ticketId: id},
          });
        });
      this.clearSearchResults();
      this.closeGlobalSearch.emit();
    } catch (error) {
      this.toastService.error(
        `Error in ${tabName}`,
        "Failed to load ticket details",
      );
    }
  }

  /**
   * Get highlighted text for a specific property
   * @param result - The search result object
   * @param propertyName - The property name to check and highlight
   * @returns HTML string with highlighted text if property matches
   */
  getHighlightedText(result: SearchResult, propertyName: string): string {
    // Check if this property is the matched one
    if (result.matchedProperty && result.matchedProperty.toLowerCase() === propertyName.toLowerCase()) {
      const value = (result as any)[propertyName];
      return this.commonService.highlightMatchedText(value, this.searchQuery);
    }
    // Return the value without highlighting
    return String((result as any)[propertyName] || '');
  }

  /**
   * Get highlighted search term for recent searches
   * Highlights the property that matched in the original search
   */
  getHighlightedSearchTerm(search: any): string {
    const searchTerm = search.searchTerm || '';
    const itemData = search.itemData || search.items;

    if (!itemData) {
      return searchTerm;
    }

    // If there's a matchedProperty in the item, highlight it
    const matchedProperty = itemData.matchedProperty || itemData.MatchedProperty;

    if (matchedProperty) {
      // The searchTerm matched this property, so highlight it
      return this.commonService.highlightMatchedText(searchTerm, searchTerm);
    }

    return searchTerm;
  }

  /**
   * Get the specific ID label based on tab type
   */
  getSpecificIdLabel(activeTab: string): string | null {
    const tab = activeTab?.toLowerCase();

    switch (tab) {
      case 'orders':
        return 'Order ID';
      case 'payments':
        return 'Payment ID';
      case 'subscriptions':
        return 'Subscription ID';
      case 'tickets':
        return 'Ticket ID';
      case 'customers':
        return 'Customer ID';
      default:
        return null;
    }
  }

  /**
   * Get the specific ID value with highlighting based on tab type
   */
  getHighlightedSpecificId(search: any): string {
    const activeTab = search.activeTab?.toLowerCase();
    const itemData = search.itemData || search.items;
    const searchTerm = search.searchTerm || '';

    if (!itemData) {
      return '';
    }

    let idValue: any = '';
    let matchedProperty = itemData.matchedProperty || itemData.MatchedProperty || '';

    switch (activeTab) {
      case 'customers':
        idValue = itemData.customerId || itemData.CustomerID || itemData.id || '';
        // Highlight if customer ID was the matched property
        if (matchedProperty.toLowerCase() === 'customerid' || matchedProperty.toLowerCase() === 'id') {
          return this.commonService.highlightMatchedText(String(idValue), searchTerm);
        }
        break;

      case 'orders':
        idValue = itemData.id || itemData.OrderID || '';
        // Highlight if the order ID was the matched property
        if (matchedProperty.toLowerCase() === 'id' || matchedProperty.toLowerCase() === 'orderid') {
          return this.commonService.highlightMatchedText(String(idValue), searchTerm);
        }
        break;

      case 'payments':
        idValue = itemData.id || itemData.PaymentID || '';
        // Highlight if payment ID or transaction ID was matched
        if (matchedProperty.toLowerCase() === 'id' || matchedProperty.toLowerCase() === 'paymentid' ||
            matchedProperty.toLowerCase() === 'tracid' || matchedProperty.toLowerCase() === 'transactionid') {
          return this.commonService.highlightMatchedText(String(idValue), searchTerm);
        }
        break;

      case 'subscriptions':
        idValue = itemData.subscriptionId || itemData.SubscriptionId || '';
        // Highlight if subscription ID was matched
        if (matchedProperty.toLowerCase() === 'subscriptionid' || matchedProperty.toLowerCase() === 'id') {
          return this.commonService.highlightMatchedText(String(idValue), searchTerm);
        }
        break;

      case 'tickets':
        idValue = itemData.id || itemData.TicketID || '';
        // Highlight if ticket ID was matched
        if (matchedProperty.toLowerCase() === 'id' || matchedProperty.toLowerCase() === 'ticketid') {
          return this.commonService.highlightMatchedText(String(idValue), searchTerm);
        }
        break;

      default:
        return '';
    }

    // Return without highlighting if no match
    return String(idValue);
  }

  /**
   * Get highlighted display ID for the badge (first row)
   * Highlights the ID in the badge when it's the matched property for any tab
   * For all tabs (except Customers), also checks if Customer ID/Name was matched
   */
  getHighlightedDisplayId(search: any): string {
    const displayId = search.displayId || '';
    const activeTab = search.activeTab?.toLowerCase();
    const itemData = search.itemData || search.items;
    const searchTerm = search.searchTerm || '';

    if (!itemData || !displayId) {
      return displayId;
    }

    const matchedProperty = (itemData.matchedProperty || itemData.MatchedProperty || '').toLowerCase();

    // Highlight badge based on which tab and which property matched
    switch (activeTab) {
      case 'customers':
        // Highlight if customer ID or customer name was the matched property
        if (matchedProperty === 'customerid' || matchedProperty === 'id' ||
            matchedProperty === 'customername' || matchedProperty === 'name') {
          return this.commonService.highlightMatchedText(String(displayId), searchTerm);
        }
        break;

      case 'orders':
        // Highlight if order ID OR customer ID/name was the matched property
        if (matchedProperty === 'id' || matchedProperty === 'orderid' ||
            matchedProperty === 'customerid' || matchedProperty === 'customername') {
          return this.commonService.highlightMatchedText(String(displayId), searchTerm);
        }
        break;

      case 'payments':
        // Highlight if payment ID/transaction ID OR customer ID/name was matched
        if (matchedProperty === 'id' || matchedProperty === 'paymentid' ||
            matchedProperty === 'tracid' || matchedProperty === 'transactionid' ||
            matchedProperty === 'customerid' || matchedProperty === 'customername') {
          return this.commonService.highlightMatchedText(String(displayId), searchTerm);
        }
        break;

      case 'subscriptions':
        // Highlight if subscription ID OR customer ID/name was matched
        if (matchedProperty === 'subscriptionid' || matchedProperty === 'id' ||
            matchedProperty === 'customerid' || matchedProperty === 'customername') {
          return this.commonService.highlightMatchedText(String(displayId), searchTerm);
        }
        break;

      case 'tickets':
        // Highlight if ticket ID OR customer ID/name was matched
        if (matchedProperty === 'id' || matchedProperty === 'ticketid' ||
            matchedProperty === 'customerid' || matchedProperty === 'customername') {
          return this.commonService.highlightMatchedText(String(displayId), searchTerm);
        }
        break;
    }

    // Return without highlighting if no match
    return displayId;
  }

  /**
   * Check if tooltip content is scrollable
   */
  isTooltipScrollable(search: any): boolean {
    // Check if there are more than 5 items (adjust this threshold as needed)
    return search.allSearchTimestamps && search.allSearchTimestamps.length > 5;
  }

  /**
   * Check if tooltip is scrolled to bottom
   */
  isTooltipScrolledToBottom(search: any): boolean {
    // This will be updated by the scroll event
    return search._tooltipScrolledToBottom || false;
  }

  /**
   * Handle tooltip scroll event
   */
  onTooltipScroll(event: Event, search: any): void {
    const element = event.target as HTMLElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    // Check if scrolled to bottom (within 10px threshold)
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

    // Store the scroll state on the search object
    search._tooltipScrolledToBottom = isAtBottom;

    // Trigger change detection
    this.cdr.detectChanges();
  }

  /**
   * Handle searches list scroll event
   */
  onSearchesListScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    // Check if scrolled to bottom (within 10px threshold)
    this.isSearchesListScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 10;

    // Trigger change detection
    this.cdr.detectChanges();
  }

  /**
   * Check if searches list is scrollable after view init or data changes
   */
  checkSearchesListScrollable(): void {
    // Use setTimeout to ensure DOM is updated
    setTimeout(() => {
      if (this.searchesListScrollable?.nativeElement) {
        const element = this.searchesListScrollable.nativeElement;
        this.isSearchesListScrollable = element.scrollHeight > element.clientHeight;

        // Reset scrolled to bottom state when checking scrollability
        this.isSearchesListScrolledToBottom = false;

        this.cdr.detectChanges();
      }
    }, 100);
  }

  /**
   * Check if search results container is scrollable after view init or data changes
   */
  checkSearchResultsScrollable(): void {
    // Use setTimeout to ensure DOM is updated
    setTimeout(() => {
      if (this.searchResultsContainer?.nativeElement) {
        const element = this.searchResultsContainer.nativeElement;
        this.isSearchResultsScrollable = element.scrollHeight > element.clientHeight;

        // Reset scrolled to bottom state when checking scrollability
        this.isSearchResultsScrolledToBottom = false;

        this.cdr.detectChanges();
      }
    }, 100);
  }
}

